import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import {
  changePasswordSchema,
  shownFieldError,
} from "../../utils/validationPatterns";
import useAuth from "../../hooks/useAuth";
import { toast } from "react-toastify";
import BsInputPassword from "../../components/public/BsInputPassword";
import { Helmet } from "react-helmet-async";
import AuthLogoRedirect from "../../components/public/AuthLogoRedirect";
import { useTranslation } from "react-i18next";
import { Surface } from "../../components/atoms/Surface";
import { queryClient } from "../../providers/QueryProvider";
import { CustomButton } from "../../components/atoms/CustomButton";
import SecureStorage from "../../utils/secureStorage";
import { useCheckPassword } from "../../hooks/auth";
import { useUserUpdatePassword } from "../../hooks/users";

const LevelUpPassword = () => {
  const [t] = useTranslation();
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const from = "/control-panel";

  useEffect(() => {
    if (!auth.user.isAuthenticated) navigate("/");
  }, [auth, from, navigate, searchParams]);

  const [apiErrors, setApiErrors] = useState<any>({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: joiResolver(changePasswordSchema),
  });

  const { mutate: checkPassword } = useCheckPassword();

  const { mutate: updatePassword, isLoading } = useUserUpdatePassword();

  const onSubmit = (data: any) => {
    const level = SecureStorage.getPasswordValidationLevel();

    checkPassword(
      {
        password: data.new_password,
        level: level,
      },
      {
        onSuccess: () => {
          updatePassword(
            {
              customer_identification: auth.user.user_identification,
              entityIdentification: auth.user.entity_identification,
              data: data,
            },
            {
              onSuccess: () => {
                toast.success(t("levelUpPassword.msg1"));

                setApiErrors({});
                reset();

                logoutDirectlyToLogin();
              },
              onError: (err) => {
                toast.error(err.response?.data?.message || err.message);

                if (err.response?.data?.message === "Credential error.") {
                  setApiErrors({
                    ...apiErrors,
                    actual_password: err.response?.data?.message,
                  });
                } else {
                  setApiErrors({
                    ...apiErrors,
                    new_password: err.response?.data?.message,
                  });
                }
              },
            }
          );
        },
      }
    );
  };

  const logoutDirectlyToLogin = () => {
    SecureStorage.clearAuthData();
    auth.logout();
    queryClient.clear();
    navigate("/login");
  };

  const formChangeHandler = () => {
    setApiErrors({});
  };

  return (
    <div className="auth-page min-vh-100 d-flex align-items-center">
      <Helmet>
        <title>Super User Manager - Password level up</title>
        <meta
          name="description"
          content="Super User Manager - Level up password"
        />
        <meta name="robots" content="noindex" />
        <link
          rel="canonical"
          href={`${process.env.REACT_APP_HOST_URL}/level-up-password`}
        />
      </Helmet>
      <div className="container mx-auto">
        <div className="grid grid-cols-12 justify-content-center">
          <div className="md:col-span-6 sm:col-span-8 col-span-12">
            <Surface>
              <div className="title">
                <h1>{t("levelUpPassword.title")}</h1>
                <small>
                  {t("levelUpPassword.backNote")}{" "}
                  <Link to={from} className="text-black">
                    {t("levelUpPassword.backBtn")}
                  </Link>
                </small>
              </div>

              <form
                onSubmit={handleSubmit(onSubmit)}
                onChange={formChangeHandler}
              >
                <BsInputPassword
                  label={t("levelUpPassword.label_1") as string}
                  isInvalid={
                    !!errors.actual_password || !!apiErrors.actual_password
                  }
                  errorMessage={
                    shownFieldError(
                      t,
                      errors.actual_password?.message as string
                    ) || apiErrors.actual_password
                  }
                  className={"mb-3"}
                  rhfControls={register("actual_password")}
                />

                <BsInputPassword
                  label={t("levelUpPassword.label_2") as string}
                  isInvalid={!!errors.new_password || !!apiErrors.new_password}
                  errorMessage={
                    shownFieldError(
                      t,
                      errors.new_password?.message as string
                    ) || apiErrors.new_password
                  }
                  className={"mb-3"}
                  rhfControls={register("new_password")}
                />

                <BsInputPassword
                  label={t("levelUpPassword.label_1") as string}
                  isInvalid={!!errors.new_confirm_password}
                  errorMessage={shownFieldError(
                    t,
                    errors.new_confirm_password?.message as string
                  )}
                  className={"mb-3"}
                  rhfControls={register("new_confirm_password")}
                />

                <CustomButton
                  type="submit"
                  variant="primary"
                  isLoading={isLoading}
                  title={t("levelUpPassword.resetBtn") as string}
                />
              </form>

              <AuthLogoRedirect
                description={t("levelUpPassword.brand.description")}
              />
            </Surface>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LevelUpPassword;
